import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import './App.css';
import { applyThemeWithAttribute, DEFAULT_THEME_COLOR } from './theme';
import Login from './components/Login';
import Register from './components/Register';
import Activate from './components/Activate';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Home from './components/Home';
import Account from './components/Account';
import Consumption from './components/Consumption';
import Dashboard from './components/Dashboard';
import AdminQuery from './components/AdminQuery';

const Friends = lazy(() => import('./components/Friends'));

const USER_KEY = 'mwbUser';

function readStoredUser() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function getPageFromLocation() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  if (path === '/register') return 'register';
  if (path === '/activate') return 'activate';
  if (path === '/forgot-password') return 'forgotPassword';
  if (path === '/reset-password') return 'resetPassword';
  if (path === '/login') return 'login';
  if (path === '/account') return 'account';
  if (path === '/consumption') return 'consumption';
  if (path === '/dashboard') return 'dashboard';
  if (path === '/admin/query') return 'adminQuery';
  if (path === '/friends') return 'friends';
  if (path === '/') return 'home';
  return 'login';
}

function App() {
  const initialPage = useMemo(getPageFromLocation, []);
  const [page, setPage] = useState(initialPage);
  const [user, setUser] = useState(readStoredUser);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const onPopState = () => setPage(getPageFromLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const goTo = (nextPage) => {
    const paths = {
      register: '/register',
      activate: '/activate',
      forgotPassword: '/forgot-password',
      resetPassword: '/reset-password',
      account: '/account',
      consumption: '/consumption',
      dashboard: '/dashboard',
      adminQuery: '/admin/query',
      friends: '/friends',
      home: '/',
      login: '/login',
    };
    window.history.pushState({}, '', paths[nextPage] || '/login');
    setPage(nextPage);
  };

  const persistUser = (userData) => {
    setUser(userData);
    if (userData) {
      sessionStorage.setItem(USER_KEY, JSON.stringify(userData));
    } else {
      sessionStorage.removeItem(USER_KEY);
    }
  };

  const handleLogin = (userData) => {
    setNotice('');
    persistUser(userData);
    goTo('home');
  };

  const handleLogout = () => {
    persistUser(null);
    setNotice('');
    goTo('login');
  };

  const handleRegistered = (message) => {
    const text = message || 'Check your email for a confirmation link to activate your account.';
    sessionStorage.setItem('mwbNotice', text);
    window.location.assign('/login');
  };

  useEffect(() => {
    const stored = sessionStorage.getItem('mwbNotice');
    if (stored) {
      sessionStorage.removeItem('mwbNotice');
      setNotice(stored);
    }
  }, []);

  useEffect(() => {
    applyThemeWithAttribute(user?.themeColor || DEFAULT_THEME_COLOR);
  }, [user?.themeColor]);

  const needsLogin =
    !user &&
    (page === 'home' ||
      page === 'account' ||
      page === 'consumption' ||
      page === 'dashboard' ||
      page === 'adminQuery' ||
      page === 'friends');

  return (
    <div className="App">
      {(page === 'login' || needsLogin) && (
        <Login
          notice={notice}
          onLogin={handleLogin}
          onGoToRegister={() => {
            setNotice('');
            goTo('register');
          }}
          onGoToForgotPassword={() => {
            setNotice('');
            goTo('forgotPassword');
          }}
        />
      )}
      {page === 'register' && !user && (
        <Register onGoToLogin={() => goTo('login')} onRegistered={handleRegistered} />
      )}
      {page === 'activate' && !user && (
        <Activate onGoToLogin={() => goTo('login')} />
      )}
      {page === 'forgotPassword' && !user && (
        <ForgotPassword onGoToLogin={() => goTo('login')} />
      )}
      {page === 'resetPassword' && !user && (
        <ResetPassword onGoToLogin={() => goTo('login')} />
      )}
      {page === 'home' && user && (
        <Home
          user={user}
          onLogout={handleLogout}
          onOpenAccount={() => goTo('account')}
          onOpenConsumption={() => goTo('consumption')}
          onOpenDashboard={() => goTo('dashboard')}
          onOpenAdminQuery={() => goTo('adminQuery')}
          onOpenFriends={() => goTo('friends')}
        />
      )}
      {page === 'friends' && user && (
        <Suspense
          fallback={
            <div className="container">
              <p className="brand">My Weed Buddy</p>
              <h1>Friends</h1>
              <p className="subtitle">Loading…</p>
            </div>
          }
        >
          <Friends user={user} onBack={() => goTo('home')} />
        </Suspense>
      )}
      {page === 'account' && user && (
        <Account
          user={user}
          onUserChange={persistUser}
          onBack={() => goTo('home')}
          onLogout={handleLogout}
        />
      )}
      {page === 'consumption' && user && (
        <Consumption
          user={user}
          onBack={() => goTo('home')}
          onSaved={() => goTo('home')}
        />
      )}
      {page === 'dashboard' && user && (
        <Dashboard user={user} onBack={() => goTo('home')} />
      )}
      {page === 'adminQuery' && user && (
        <AdminQuery user={user} onBack={() => goTo('home')} />
      )}
    </div>
  );
}

export default App;
